import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSchemas } from "@/contexts/SchemaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Save, Layers, ClipboardList } from "lucide-react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import FieldForm from "@/components/schema/FieldForm";
import ReachPanel from "@/components/schema/ReachPanel";
import ZoneGrid from "@/components/schema/ZoneGrid";
import RecoveryPanel from "@/components/RecoveryPanel";
import ImportExport from "@/components/schema/ImportExport";
import { useReviewFlow } from "@/components/schema/ReviewFlow";
import { useConfirm } from "@/components/ConfirmDialog";
import { downloadCsv, fmtMeta, joinZones, parseZones, resolveFields } from "@/lib/schema";

const LABEL = "text-xs uppercase tracking-[0.15em] text-slate-600";
const PROGRAM_TEXT = "How much and for how long this patient should use the headset. Adherence on the dashboard is measured against the daily target.";
const RESULTS_TEXT = "Trends built from what the headset reports after every round (success, speed, reach...). Pick a treatment area to see its charts. Export CSV gives you every value, including extra measures, for your own analysis.";
const ACTIVITY_TEXT = "Every automatic level change or suggestion the headset made for this patient, newest first. You are also notified (see the bell) unless you switched that type off in notification settings.";
const THERAPY_TEXT = "A therapy is a saved treatment plan. Applying one sets up this patient's headset in one step: which activities are on it, each one's level, zones and auto-level choice, and optionally the patient's treatment settings. You see every change first and nothing is saved unless it is all valid. The patient's name, notes, progress and history are never touched.";
const COLORS = ["hsl(271 70% 55%)", "hsl(198 90% 50%)", "hsl(295 75% 55%)", "hsl(160 60% 40%)", "hsl(35 90% 50%)"];

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const confirmDialog = useConfirm();
  const { user } = useAuth();
  const { patient: pdata } = useSchemas();
  const [patient, setPatient] = useState(null);
  const [program, setProgram] = useState({});
  const [settings, setSettings] = useState({});
  const [results, setResults] = useState(null);
  const [therapies, setTherapies] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({ Name: "", Description: "" });
  const [pick, setPick] = useState("");
  const [names, setNames] = useState({});
  const [activity, setActivity] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get(`/patients/${id}`);
      setPatient(data);
      setSettings(data.settings || {});
      setProgram({
        TargetMinutesPerDay: data.target_minutes_per_day, ProgramWeeks: data.program_weeks,
        ProgramStartDate: data.program_start_date, Notes: data.notes || "",
      });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); navigate("/roster", { replace: true }); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get(`/patients/${id}/results`).then((r) => setResults(r.data)).catch(() => {});
    api.get("/therapies").then((r) => setTherapies(r.data)).catch(() => {});
    api.get(`/patients/${id}/level-events`).then((r) => setActivity(r.data)).catch(() => {});
    api.get("/users").then((r) => setNames(Object.fromEntries(r.data.map((u) => [u.id, u.name])))).catch(
      () => setNames({ [user.id]: user.name }));
  }, [id, user]);

  const groups = useMemo(() => resolveFields(pdata?.patientInputs || [], pdata?.arcFields || []), [pdata]);
  // plain top-level fields (and any "patientFields" group) live on the patient document
  const programFields = groups.flatMap((g) => (g.type !== "group" ? [g] : g.panel === "patientFields" ? g.fields : []));
  const treatGroups = groups.filter((g) => g.type === "group" && g.panel !== "patientFields");

  const saveFlow = useReviewFlow(() => load());
  const therapyFlow = useReviewFlow(() => load());

  const body = () => {
    const out = { settings };
    if (programFields.length) {
      out.target_minutes_per_day = Number(program.TargetMinutesPerDay);
      out.program_weeks = Number(program.ProgramWeeks);
      out.program_start_date = program.ProgramStartDate;
      out.notes = program.Notes;
    }
    return out;
  };
  const save = async () => {
    try {
      const { data } = await api.patch(`/patients/${id}`, body());
      setPatient(data); setSettings(data.settings || {});
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeTherapy = async () => {
    try {
      const { data: prev } = await api.delete(`/patients/${id}/therapy`, { params: { dry_run: true } });
      const list = (x) => x.map((a) => a.module || a.simulation).join(", ");
      const ok = await confirmDialog({
        title: `Remove "${prev.therapy.name}"?`,
        description: `${prev.will_remove.length ? `These activities were added by the therapy and will be taken off the headset: ${list(prev.will_remove)}. ` : "No activities will be taken off. "}`
          + `${prev.will_keep.length ? `These stay (they were already there; only the therapy tag is cleared): ${list(prev.will_keep)}. ` : ""}`
          + "The patient's treatment settings are not changed (use History to undo those).",
        confirmText: "Remove therapy", destructive: true,
      });
      if (!ok) return;
      await api.delete(`/patients/${id}/therapy`, { params: { dry_run: false } });
      toast.success("Therapy removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const applyTherapy = () => therapyFlow.start("Apply therapy", ({ dryRun }) =>
    api.post(`/patients/${id}/apply-therapy/${pick}`, null, { params: { dry_run: dryRun } }));

  const saveAsTherapy = async () => {
    try {
      await api.post(`/patients/${id}/save-as-therapy`, saveForm);
      toast.success("Therapy saved");
      setSaveOpen(false);
      api.get("/therapies").then((r) => setTherapies(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!patient || !pdata) return <div className="text-slate-400 text-sm">Loading…</div>;

  const panelFor = (g) => g.panel === "reach"
    ? <ReachPanel group={g} values={settings[g.key]} role={user.role} patientData={pdata}
        onChange={(v) => setSettings({ ...settings, [g.key]: v })} />
    : <FieldForm fields={g.fields} values={settings[g.key]} role={user.role} patientData={pdata}
        onChange={(v) => setSettings({ ...settings, [g.key]: v })} />;

  return (
    <div>
      <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.15em] text-slate-500 hover:text-purple-700 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="h-3 w-3" /> Back
      </button>
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-6 border-b border-slate-200/60">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-purple-700 mb-2">Patient</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900" data-testid="patient-name">{patient.name}</h1>
          <div className="text-xs text-slate-500 mt-2" data-testid="settings-meta">{fmtMeta(settings.Meta, names) || "No settings saved yet"}</div>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <ImportExport
            showTransfer={user.role === "admin"}
            exportUrl={`/patients/${id}/settings/export`}
            filename={`patient-settings-${patient.name.replace(/\W+/g, "_")}.json`}
            importCall={({ dryRun, allowOlder }, json) =>
              api.post(`/patients/${id}/settings/import`, json, { params: { dry_run: dryRun, allow_older: allowOlder } })}
            history={{ kind: "patient_settings", recordId: id }}
            onChanged={load}
          />
          <Button variant="ghost" size="sm" className="text-slate-500" data-testid="export-csv-header"
            onClick={() => downloadCsv(api, { patient_id: id, days: 365 }, `${patient.name.replace(/\W+/g, "_")}-data.csv`).catch(() => toast.error("Export failed"))}>
            Export data (CSV)
          </Button>
          <Button onClick={save} className="btn-brand rounded-full" data-testid="save-patient"><Save className="h-4 w-4 mr-2" /> Save</Button>
        </div>
      </div>

      <Tabs defaultValue="recovery">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="recovery" data-testid="tab-recovery">Recovery</TabsTrigger>
          <TabsTrigger value="program">Program</TabsTrigger>
          {treatGroups.map((g) => <TabsTrigger key={g.key} value={g.key} data-testid={`tab-${g.key}`}>{g.label || g.key}</TabsTrigger>)}
          <TabsTrigger value="results" data-testid="tab-results">Charts</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Level activity</TabsTrigger>
          <TabsTrigger value="therapy" data-testid="tab-therapy">Therapy</TabsTrigger>
        </TabsList>

        <TabsContent value="recovery"><RecoveryPanel patientId={id} /></TabsContent>

        <TabsContent value="program">
          <div className="panel rounded-2xl p-5">
            <p className="text-sm text-slate-600 mb-4">{PROGRAM_TEXT}</p>
            {programFields.length > 0 && (
              <FieldForm fields={programFields} values={program} onChange={setProgram} role={user.role} patientData={pdata} />
            )}
            <div className="mt-5">
              <Label className={LABEL}>Patient name</Label>
              <PatientName id={id} name={patient.name} onSaved={load} />
            </div>
          </div>
        </TabsContent>

        {treatGroups.map((g) => (
          <TabsContent key={g.key} value={g.key}>
            <div className="panel rounded-2xl p-5">{g.description && <p className="text-sm text-slate-600 mb-4">{g.description}</p>}{panelFor(g)}</div>
          </TabsContent>
        ))}

        <TabsContent value="results">
          <div className="flex items-start justify-between gap-4 mb-3">
            <p className="text-sm text-slate-600 max-w-2xl">{RESULTS_TEXT}</p>
            <Button variant="outline" size="sm" data-testid="export-csv"
              onClick={() => downloadCsv(api, { patient_id: id, days: 365 }, `${patient.name.replace(/\W+/g, "_")}-results.csv`).catch(() => toast.error("Export failed"))}>
              Export CSV
            </Button>
          </div>
          <Results data={results} pdata={pdata} />
        </TabsContent>

        <TabsContent value="activity">
          <p className="text-sm text-slate-600 mb-3">{ACTIVITY_TEXT}</p>
          <div className="panel rounded-2xl divide-y divide-slate-100" data-testid="level-activity">
            {activity.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No level changes or suggestions yet.</div>}
            {activity.map((l) => (
              <div key={l.id} className="px-4 py-2.5 text-sm flex gap-3">
                <span className="text-xs text-slate-500 w-40 shrink-0 tabular-nums">{new Date(l.at).toLocaleString()}</span>
                <span className="text-xs font-mono text-slate-600 w-28 shrink-0 truncate">{l.game || "-"}</span>
                <span className="text-slate-800">{l.message}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="therapy">
          <div className="panel rounded-2xl p-5 space-y-5">
            {patient.therapy_applied && (
              <div className="rounded-lg border border-purple-200 bg-purple-50/50 p-3" data-testid="applied-therapy">
                <div className="text-sm">Therapy applied: <b>{patient.therapy_applied.name}</b> on {new Date(patient.therapy_applied.at).toLocaleDateString()}</div>
                <p className="text-xs text-slate-600 mt-1">You can keep editing the activities by hand afterwards. Removing the therapy takes off the activities it added; activities that were already there stay.</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={removeTherapy} data-testid="remove-therapy">Remove therapy</Button>
              </div>
            )}
            <p className="text-sm text-slate-600">{THERAPY_TEXT}</p>
            <div>
              <div className={LABEL}>Apply a therapy</div>
              <div className="flex gap-2 mt-2">
                <Select value={pick} onValueChange={setPick}>
                  <SelectTrigger className="max-w-xs" data-testid="therapy-select"><SelectValue placeholder="Choose a therapy…" /></SelectTrigger>
                  <SelectContent>{therapies.map((t) => <SelectItem key={t.id} value={t.id}>{t.Name}</SelectItem>)}</SelectContent>
                </Select>
                <Button disabled={!pick} onClick={applyTherapy} variant="outline" data-testid="apply-therapy"><ClipboardList className="h-4 w-4 mr-2" /> Preview and apply</Button>
              </div>
              <p className="text-xs text-slate-500 mt-2">Shows exactly what will change first. Needs the patient to have a headset when the therapy includes modules.</p>
            </div>
            <div>
              <div className={LABEL}>Save this plan</div>
              <p className="text-xs text-slate-500 mt-1">Turns this patient's current activities and settings into a therapy you can reuse for other patients.</p>
              <Button className="mt-2" variant="outline" onClick={() => setSaveOpen(true)} data-testid="save-as-therapy"><Layers className="h-4 w-4 mr-2" /> Save current plan as therapy</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Save as therapy</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className={LABEL}>Name</Label><Input value={saveForm.Name} onChange={(e) => setSaveForm({ ...saveForm, Name: e.target.value })} data-testid="therapy-name" /></div>
            <div><Label className={LABEL}>Description</Label><Textarea value={saveForm.Description} onChange={(e) => setSaveForm({ ...saveForm, Description: e.target.value })} /></div>
            <p className="text-xs text-slate-500">Saves the saved patient settings and this headset's module configs. Save your edits first.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button className="btn-brand rounded-full" disabled={!saveForm.Name.trim()} onClick={saveAsTherapy}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {saveFlow.dialog}
      {therapyFlow.dialog}
    </div>
  );
}

function PatientName({ id, name, onSaved }) {
  const [v, setV] = useState(name);
  useEffect(() => setV(name), [name]);
  return (
    <div className="flex gap-2 mt-2 max-w-md">
      <Input value={v} onChange={(e) => setV(e.target.value)} />
      <Button variant="outline" disabled={!v.trim() || v === name} onClick={async () => {
        try { await api.patch(`/patients/${id}`, { name: v.trim() }); toast.success("Renamed"); onSaved(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      }}>Rename</Button>
    </div>
  );
}

function Results({ data, pdata }) {
  if (!data) return <div className="text-sm text-slate-400">Loading…</div>;
  const domains = Object.entries(data.domains || {});
  const extra = data.extra || [];
  const cells = data.cells?.length ? data.cells : pdata?.reachZones?.cells || [];
  const union = new Set((data.configured_zones || []).flatMap((z) => parseZones(z.zones, cells)));
  const configured = joinZones([...union], cells);
  if (domains.length === 0 && extra.length === 0 && !data.reached_zones) {
    return <div className="panel rounded-2xl p-10 text-center text-sm text-slate-500">No results yet. They appear after the headset reports sessions.</div>;
  }
  return (
    <div className="space-y-6">
      {domains.map(([domain, series]) => (
        <div key={domain} className="panel rounded-2xl p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-purple-700 mb-3">{domain}</div>
          <div className="grid md:grid-cols-2 gap-4">{series.map((s, i) => <Trend key={s.key} s={s} color={COLORS[i % COLORS.length]} />)}</div>
        </div>
      ))}
      {extra.length > 0 && (
        <div className="panel rounded-2xl p-5">
          <div className="text-xs uppercase tracking-[0.18em] text-purple-700 mb-3">Custom metrics</div>
          <div className="grid md:grid-cols-2 gap-4">{extra.map((s, i) => <Trend key={s.key} s={s} color={COLORS[i % COLORS.length]} />)}</div>
        </div>
      )}
      {data.reached_zones && (
        <div className="panel rounded-2xl p-5" data-testid="reached-zones">
          <div className="text-xs uppercase tracking-[0.18em] text-purple-700 mb-3">Reached zones vs configured zones</div>
          <div className="flex flex-wrap gap-8">
            <div><div className="text-xs text-slate-500 mb-1">Configured</div>
              <ZoneGrid value={configured || "None"} cells={cells} groups={{}} readOnly /></div>
            <div><div className="text-xs text-slate-500 mb-1">Reached (latest)</div>
              <ZoneGrid value={data.reached_zones.zones} cells={cells} groups={{}} readOnly /></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Trend({ s, color }) {
  const pts = s.points.map((p) => ({ t: p.t.slice(0, 10), v: p.v }));
  return (
    <div>
      <div className="text-sm font-medium text-slate-800">{s.label}{s.unit ? <span className="text-xs text-slate-400 ml-1">({s.unit})</span> : null}</div>
      <div className="h-40">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={pts}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="t" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} width={36} />
            <Tooltip />
            <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
